import { PlanningActionMetadataDtoModelType, PlanningActionsStatus } from '../../../models';
import * as ScheduleNotifications from '../../../../../notifications/scheduling/schedule';
import * as OrderNotifications from '../../../../../notifications/visits';
import getAppEnv from '../../../utils/getAppEnv';
import getAppRoot from '../../../utils/getAppRoot';
import handleMoveRoutesNotification from './handleMoveRoutesNotification';
import { unpublishRouteFailureNotification } from '../../../../../notifications/routes/unpublishRoute';
import { deletingStopMessageFailureNotification } from '../../../../../notifications/scheduling/deleteStop';
import i18n from '../../../../../i18n';

export const handleUnscheduleNotification = (
  selectedDate: string | undefined,
  store: any,
  appStore: any,
  unscheduledOrdersCount?: number | null,
  earliestDate?: string | null,
) => {
  if (!unscheduledOrdersCount) {
    return;
  }

  const shouldLinkToEarliestDate = Boolean(earliestDate && selectedDate && earliestDate !== selectedDate);

  getAppEnv(store).notification(OrderNotifications.notifyOrderUnscheduledSuccess(
    unscheduledOrdersCount,
    earliestDate ?? selectedDate ?? '',
    shouldLinkToEarliestDate && earliestDate ? () => appStore.router.redirectToDate(earliestDate) : undefined,
  ));
};

const handleScheduleFinishedNotification = (
  action: PlanningActionMetadataDtoModelType,
  appStore: any,
  projectId?: string,
  selectedDate?: string | null,
) => {
  appStore.model.fetchProjectRoutesExtraData(projectId, appStore.ui.selectedDate);

  if (action.startDate === selectedDate) {
    getAppEnv(action.store).notification(ScheduleNotifications.scheduleSuccessNotification(
      action.actionUuid,
      action.output,
    ));
  }
};

// Keep the persistent Unscheduled-panel scheduling-error banner in sync with the action outcome
// (mirrors the optimize/ordersToTarget handlers): show the reason on failure, clear it on success.
// A failed UNDO is not a scheduling failure, so it must not set the (mis-framed) banner.
const applySchedulingBanner = (
  appStore: any,
  store: any,
  projectId: string,
  planningAction: PlanningActionMetadataDtoModelType,
) => {
  const { status, output } = planningAction;

  if (status === PlanningActionsStatus.FAILED || status === PlanningActionsStatus.FINISHED_WITH_FAILED) {
    getAppEnv(store).notification(ScheduleNotifications.scheduleFailureNotification(projectId, output));
    if (!planningAction.isUndo) {
      appStore.ui.setSchedulingError(ScheduleNotifications.buildOutputError(output));
    }
  } else if (status === PlanningActionsStatus.FINISHED) {
    appStore.ui.setSchedulingError(null);
  }
};

// Unpublish rides the same planning-action stream as scheduling, but none of the scheduling copy
// applies to it: it produces no scheduling output, so the success toast would be mis-worded, and a
// failure is not a scheduling failure, so it must not set the Unscheduled-panel error banner.
const handleUnpublishAction = (
  appStore: any,
  store: any,
  projectId: string,
  planningAction: PlanningActionMetadataDtoModelType,
) => {
  const { status, actionUuid } = planningAction;

  if (status !== PlanningActionsStatus.NOT_STARTED) {
    appStore.ui.setIsUnpublishing(false);
  }

  if (status === PlanningActionsStatus.FINISHED) {
    appStore.model.fetchProjectRoutesExtraData(projectId, appStore.ui.selectedDate);
  }

  if (status === PlanningActionsStatus.FAILED || status === PlanningActionsStatus.FINISHED_WITH_FAILED) {
    getAppEnv(store).notification(unpublishRouteFailureNotification(actionUuid));
  }
};

export default ({ store, ...planningAction }: PlanningActionMetadataDtoModelType) => {
  const appStore = getAppRoot(store);
  const projectId = appStore.ui.selectedWorkspace?.id;

  // Only handle notifications for the current workspace's own actions.
  if (!projectId || appStore.account?.user?.uuid !== planningAction.userUuid) {
    return;
  }

  // v1 may finish before visit processing. VisitDeletedDto reports committed deletions.
  if (planningAction.actionName === 'DeleteOrdersFromRoutes') {
    if (planningAction.status === PlanningActionsStatus.FAILED || planningAction.status === PlanningActionsStatus.FINISHED_WITH_FAILED) {
      getAppEnv(store).notification(deletingStopMessageFailureNotification(
        i18n.t('components:deleteVisitsConfirmModal.requestError'), '',
      ));
    }
    appStore.planningActions.getUndoableRedoableActions(true);
    return;
  }

  if (handleMoveRoutesNotification({ ...planningAction, store }, appStore, projectId)) {
    appStore.planningActions.getUndoableRedoableActions(true);
    return;
  }

  if (planningAction.actionName === 'Unpublish') {
    handleUnpublishAction(appStore, store, projectId, planningAction);
    appStore.planningActions.getUndoableRedoableActions(true);
    return;
  }

  const { selectedDate } = appStore.ui;
  const unscheduleActionFinished = planningAction.actionName === 'UnscheduleStopsFromRoutes'
    && planningAction.status === PlanningActionsStatus.FINISHED;

  if (planningAction.actionName === 'Publish' && planningAction.status !== PlanningActionsStatus.NOT_STARTED) {
    appStore.ui.setIsPublishing(false);
  }

  if (unscheduleActionFinished) {
    handleUnscheduleNotification(
      selectedDate,
      store,
      appStore,
      planningAction.output?.details?.unscheduledOrdersCount,
      planningAction.output?.details?.earliestDate,
    );
  }

  if (planningAction.status === PlanningActionsStatus.FINISHED && !unscheduleActionFinished) {
    handleScheduleFinishedNotification(
      { ...planningAction, store },
      appStore,
      projectId,
      selectedDate,
    );
  }

  applySchedulingBanner(appStore, store, projectId, planningAction);

  // Always query for lastUndoable and nextRedoable actions
  appStore.planningActions.getUndoableRedoableActions(true);
};
